import { Searcher } from "fast-fuzzy"
import { useMemo, useState } from "react"

import { useUserContext } from "#contexts/UserContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DataWithId } from "#customTypes/types"

const DEFAULT_RESULTS_COUNT = 20

const createSearchableStringArray = <T extends DataWithId>(
  item: T,
  SEARCHABLE_FIELDS: (keyof T)[]
): string[] => {
  return SEARCHABLE_FIELDS.map((field) => String(item[field] ?? ""))
}

const useOptimizedSearch = <T extends DataWithId>(
  items: T[],
  SEARCHABLE_FIELDS: (keyof T)[],
  resultsCount = DEFAULT_RESULTS_COUNT,
  defaultSearchTerm = ""
) => {
  // Use effectiveRole to support impersonation
  const { effectiveRole } = useUserContext()
  const canSearchIds = effectiveRole === USER_ROLES.DEV

  const searchableFields = useMemo(() => {
    return canSearchIds ? [...SEARCHABLE_FIELDS, "id"] : SEARCHABLE_FIELDS
  }, [SEARCHABLE_FIELDS, canSearchIds])

  const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm)
  // const handleSearch = useMemo(
  //   () =>
  //     debounce((searchTerm: string) => {
  //       console.log("✅🔥 searchTerm => ", searchTerm)
  //       setSearchTerm(searchTerm)
  //     }, 1000),

  //   [setSearchTerm]
  // )

  const searchStringsById = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc[item.id] = createSearchableStringArray(item, searchableFields)
        return acc
      },
      {} as Record<Uuid, string[]>
    )
  }, [items, searchableFields])

  const searcher = useMemo(
    () =>
      new Searcher(items, {
        threshold: 0.9,
        ignoreCase: true,
        ignoreSymbols: false,
        keySelector: (item: T) => {
          return searchStringsById[item.id]
        },
      }),
    [items, searchStringsById]
  )

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items.slice(0, resultsCount)

    return searcher.search(searchTerm).slice(0, resultsCount)
  }, [searcher, searchTerm, items, resultsCount])

  return { filteredItems, onSearch: setSearchTerm, searchTerm }
}

export default useOptimizedSearch
